/**
 * What an integrity flag means, in words a manager can act on.
 *
 * The flags were computed and stored from the start and never named anywhere.
 * Two screens showed a count — "2 flags" on ninety rows — with the raw strings
 * only in a `title` attribute, which is invisible on a touchscreen and
 * unreadable to a screen reader. A reader could learn that something was
 * noticed and never what.
 *
 * So the names live here rather than inline, for the reason `checkIns.ts`
 * exists: three screens show these, and the same manager reads two of them.
 *
 * **Every one of these is evidence, never a verdict.** `services/geo.py` is
 * explicit that a bad GPS fix in a basement looks identical to a spoofed one,
 * and that punishing the first to catch the second is not a trade worth
 * making. Nothing marks absence or fraud on the strength of a flag, and the
 * wording below is chosen to describe what was observed rather than to accuse
 * anybody of anything.
 */

/** The vocabulary, mirroring the constants in `services/geo.py`. */
const LABELS: Record<string, string> = {
  low_accuracy: 'GPS fix too rough to place',
  //: Its own flag rather than low_accuracy, and the distinction is the point:
  //: a 300 m radius is somebody indoors, a 300 km one is a browser with no
  //: GPS at all falling back to an IP lookup.
  ip_geolocation: 'Located by network, not GPS',
  no_location: 'No location sent',
  impossible_travel: 'Too far from the previous check-in to have travelled',
  identical_coordinates: 'Exactly the same coordinates as before',
  null_island: 'Coordinates read 0, 0 — not a real place',
};

/**
 * One flag, named.
 *
 * An unknown flag falls back to its own string with the underscores opened
 * out, rather than to "unknown" or to nothing. A flag added on the server and
 * not yet listed here should still be readable — losing it silently is how the
 * screen ends up back where it started.
 */
export function flagLabel(flag: string): string {
  return LABELS[flag] ?? flag.replace(/_/g, ' ');
}

/** A whole list, for a tooltip or a line of text. */
export function flagLabels(flags: string[] | null | undefined): string {
  if (!flags || flags.length === 0) return '';
  return flags.map(flagLabel).join(' · ');
}
